import {
  CompType,
  PROPERTY_GRID_SUBJECT_PROPERTY,
  PropertyData,
  propertyHasCustomManualAdjustments,
} from '@aloft/toolkit-shared';
import { Flex, Grid, Icon, ProductColor, Typography, theme } from '@aloft/toolkit-sui';

import { upperCase } from 'lodash';
import { PropsWithChildren } from 'react';
import { Control, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';

import {
  selectCompType,
  selectEffectiveDate,
  selectOrder,
} from '../../../redux/features/selectors/orderSelectors.ts';
import {
  selectPropertyDataById,
} from '../../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import { selectTransactionById } from '../../../redux/features/selectors/propertyDataSelectors/transactionSelectors.ts';
import { selectAivArvActiveTab } from '../../../redux/features/selectors/uiSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { SubjectPropertyDataState } from '../../../redux/types.ts';
import { getColorForStatusOrSubject } from '../../../utils/getColorForStatusOrSubject.ts';
import { CraftsmanPropertyHeader } from '../../CraftsmanPropertyHeader/CraftsmanPropertyHeader.tsx';
import { ValuationTablePropertyHeaderColorBand } from '../../CraftsmanPropertyHeader/internal/ValuationTablePropertyHeaderColorBand.tsx';
import { ResetAdjustmentsButton } from '../ResetAdjustmentsButton.tsx';
import { PropertyGridType, PropertyGridForm } from '../types.ts';
import { getDataSourceDisplayInfo, getSimilarityScore } from '../utils.ts';

type HeaderPropertyGridSectionProps = {
  columnIndex: number;
  gridType: PropertyGridType;
  control: Control<PropertyGridForm>;
  getValues: UseFormGetValues<PropertyGridForm>;
  setValue: UseFormSetValue<PropertyGridForm>;
};

const SubHeaderItem = ({
  children,
  backgroundColor,
  color,
}: PropsWithChildren<{
  backgroundColor?: ProductColor;
  color?: ProductColor;
}>) => (
  <Flex
    height="32px"
    padding="4px 8px"
    alignItems="center"
    justifyContent="space-between"
    backgroundColor={backgroundColor ?? 'surfaceAccent7'}
    __dangerDoNotUseSx={{
      color: theme.colors[color ?? 'textOffset'],
    }}
  >
    {children}
  </Flex>
);

const _SubHeader = ({ gridType, columnIndex, control, getValues, setValue }: HeaderPropertyGridSectionProps) => {
  const effectiveDate = useAppSelector(selectEffectiveDate);
  const { hasArvOption } = useAppSelector(selectOrder);
  const subject = useAppSelector((state) => selectPropertyDataById(state, 'subject')) as
    | SubjectPropertyDataState
    | undefined;
  const activeTab = useAppSelector(selectAivArvActiveTab);

  const isAdjustmentsGrid = gridType === 'AdjustmentsGrid';
  const showAivComps =
    (gridType !== 'FeatureGrid' && !isAdjustmentsGrid) ||
    !hasArvOption ||
    activeTab === 'all' ||
    activeTab === 'aiv';
  const showArvComps =
    (gridType !== 'FeatureGrid' && !isAdjustmentsGrid) ||
    !hasArvOption ||
    activeTab === 'all' ||
    activeTab === 'arv';

  const columnData = useWatch({ control, name: `columns.${columnIndex}` });
  const manualAdjustments = useWatch({
    control,
    name: `columns.${columnIndex}.propertyData.editableData.manualAdjustments`,
  });

  const { propertyData, propertyId } = columnData || {};

  const isSubject = propertyId === 'subject';
  const isFeatureGrid = gridType === 'FeatureGrid';
  const hasCustomManualAdjustments = propertyHasCustomManualAdjustments(manualAdjustments);

  const compType: CompType = useAppSelector((state) => selectCompType(state, propertyId)) ?? 'aiv';
  const compTypeBackgroundColor: ProductColor = `${compType}Light` as ProductColor;
  const compTypePillBackgroundColor: ProductColor = compType as ProductColor;
  const compTypeTextColor: ProductColor = `${compType}Text` as ProductColor;

  if (!subject || !effectiveDate || !propertyData) return null;

  const similarityScore =
    !isSubject && propertyId
      ? getSimilarityScore(
        subject.subjectData,
        effectiveDate,
        propertyId,
        propertyData as PropertyData,
      )
      : undefined;

  if (isSubject) {
    let columnCount = 1;
    if ((isFeatureGrid || isAdjustmentsGrid) && hasArvOption) {
      if (showAivComps) columnCount += 1;
      if (showArvComps) columnCount += 1;
    }
    const gridTemplateColumns = `repeat(${columnCount}, 1fr)`;

    return (
      <Grid
        width="100%"
        height="32px"
        gridTemplateColumns={gridTemplateColumns}
        gap="1px"
        backgroundColor="strokeDefault"
        alignItems="center"
        __dangerDoNotUseSx={{ color: theme.colors.textOffset }}
      >
        <Typography
          variant="body2"
          __dangerDoNotUseSx={{
            alignContent: 'center',
            backgroundColor: theme.colors.surfaceAccent7,
            height: '100%',
            paddingLeft: '4px',
          }}
        >
          Feature
        </Typography>
        {hasArvOption && (
          <>
            {showAivComps && (
              <Typography
                variant="body2"
                color="aivText"
                __dangerDoNotUseSx={{
                  alignContent: 'center',
                  backgroundColor: theme.colors.aiv,
                  height: '100%',
                  paddingLeft: '4px',
                }}
              >
                As-Is Value
              </Typography>
            )}
            {showArvComps && (
              <Typography
                variant="body2"
                color="arvText"
                __dangerDoNotUseSx={{
                  alignContent: 'center',
                  backgroundColor: theme.colors.arv,
                  height: '100%',
                  paddingLeft: '4px',
                }}
              >
                After-Repair Value
              </Typography>
            )}
          </>
        )}
      </Grid>
    );
  }

  if (isFeatureGrid) {
    if (!hasArvOption) {
      return (
        <SubHeaderItem>
          <span>Similarity Score</span>
          {similarityScore}
        </SubHeaderItem>
      );
    }

    return (
      <SubHeaderItem backgroundColor={compTypeBackgroundColor}>
        <Typography variant="body2" color={compTypeTextColor}>
          Similarity Score:&nbsp;
          {similarityScore ?? '-'}
        </Typography>
        <Flex
          backgroundColor={compTypePillBackgroundColor}
          __dangerDoNotUseSx={{
            padding: '2px 6px',
            borderRadius: '4px',
          }}
        >
          <Typography variant="body2" color={compTypeTextColor}>
            {upperCase(compType)}
          </Typography>
        </Flex>
      </SubHeaderItem>
    );
  }

  const subHeaderItemBackgroundColor: ProductColor | undefined = hasArvOption
    ? compTypeBackgroundColor
    : undefined;
  const subHeaderItemColor: ProductColor | undefined = hasArvOption ? 'textPrimary' : undefined;

  return (
    <Grid backgroundColor="strokeDefault" gap="1px" gridTemplateColumns="1fr 1fr">
      <SubHeaderItem backgroundColor={subHeaderItemBackgroundColor} color={subHeaderItemColor}>
        Feature
      </SubHeaderItem>
      <SubHeaderItem backgroundColor={subHeaderItemBackgroundColor} color={subHeaderItemColor}>
        Adjustment
        {hasArvOption && !hasCustomManualAdjustments && (
          <Flex
            backgroundColor={compTypePillBackgroundColor}
            __dangerDoNotUseSx={{
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            <Typography variant="body2" color={compTypeTextColor}>
              {upperCase(compType)}
            </Typography>
          </Flex>
        )}
        {hasCustomManualAdjustments && <ResetAdjustmentsButton columnIndex={columnIndex} control={control} getValues={getValues} setValue={setValue} />}
      </SubHeaderItem>
    </Grid>
  );
};

export const HeaderPropertyGridSection = ({
  columnIndex,
  gridType,
  control,
  getValues,
  setValue,
}: HeaderPropertyGridSectionProps) => {
  const columnData = useWatch({ control, name: `columns.${columnIndex}` });

  const { propertyData, type, propertyId } = columnData ?? {};

  const transaction = useAppSelector((state) => selectTransactionById(state, propertyId));

  if (!propertyData || !transaction) return null;

  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;
  const propertyStatus = isSubject ? 'Subject' : (transaction.status ?? 'Off-Market');
  const color = getColorForStatusOrSubject(propertyStatus);

  const isFeatureGrid = gridType === 'FeatureGrid';

  if (type === 'property') {
    return (
      <Flex
        flexDirection="column"
        position="sticky"
        top="0"
        zIndex={theme.zIndices.sticky}
        __dangerDoNotUseSx={{ borderTop: `1px solid ${theme.colors.strokeDefault}` }}
      >
        <CraftsmanPropertyHeader
          propertyId={propertyId}
          showDataSourceDrawerToggler={isFeatureGrid}
        />
      </Flex>
    );
  }

  const { icon, text } = getDataSourceDisplayInfo(type, propertyData);

  return (
    <Flex
      flexDirection="column"
      position="sticky"
      top="0"
      zIndex={theme.zIndices.sticky}
      alignItems="center"
      __dangerDoNotUseSx={{
        borderTop: `1px solid ${theme.colors.strokeDefault}`,
        color: theme.colors.buttonDefault,
      }}
    >
      <Flex flexDirection="column" width="100%" height="115px" backgroundColor="surface1">
        <Flex flexDirection="column" alignItems="center" justifyContent="center" flexGrow={1}>
          <Icon size="xl" icon={icon} color="buttonDefault" />
          {text}
        </Flex>
        <ValuationTablePropertyHeaderColorBand color={color} />
      </Flex>
    </Flex>
  );
};
